import { useEffect, useState } from "react";
import Modal from "react-responsive-modal";
import { AlertCircle, Lock, MessageSquareText } from "lucide-react";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { useAuth } from "../../hooks/auth";
import {
  isBlockedScheduleSlot,
  parseScheduleBaseSlotPayload,
  resolveScheduleSlotNote,
  ScheduleBaseSlot,
} from "../../services/scheduleSlot";
import { extractApiErrorMessage } from "../../utils/scheduleLockPolicy";

type Props = {
  open: boolean;
  scheduleCoord: string;
  formattedDate: string;
  isUnlocking: boolean;
  onClose: () => void;
  onUnlock: () => void;
};

type ViewState =
  | { type: "loading" }
  | { type: "ready"; slot: ScheduleBaseSlot }
  | { type: "error"; message: string };

export default function ModalBlockedScheduleSlot({
  open,
  scheduleCoord,
  formattedDate,
  isUnlocking,
  onClose,
  onUnlock,
}: Props) {
  const { access_token, dateToExpires } = useAuth();
  const [viewState, setViewState] = useState<ViewState>({ type: "loading" });

  useEffect(() => {
    if (!open || !scheduleCoord) {
      return;
    }

    let cancelled = false;

    async function loadSlot() {
      setViewState({ type: "loading" });

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get(
          `/api/adm/schedules/slot/${encodeURIComponent(scheduleCoord)}`
        );

        if (cancelled) {
          return;
        }

        const slot = parseScheduleBaseSlotPayload(response.data?.data);
        setViewState({ type: "ready", slot });
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }

        setViewState({
          type: "error",
          message:
            extractApiErrorMessage(err) ??
            "Não foi possível carregar os detalhes deste horário.",
        });
      }
    }

    loadSlot();

    return () => {
      cancelled = true;
    };
  }, [open, scheduleCoord, access_token, dateToExpires]);

  const readySlot = viewState.type === "ready" ? viewState.slot : null;
  const lockNote = readySlot ? resolveScheduleSlotNote(readySlot) : null;
  const isBlocked = readySlot ? isBlockedScheduleSlot(readySlot) : false;
  const slotMissing = readySlot?.exists === false;

  return (
    <Modal
      open={open}
      onClose={onClose}
      center
      classNames={{
        modal: "schedule-lock-note-modal",
      }}
    >
      <div className="mx-auto flex w-full flex-col">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
            <Lock className="h-5 w-5 text-red-600" aria-hidden />
          </div>
          <div className="min-w-0 text-left">
            <p className="text-lg font-bold text-slate-800 sm:text-xl">
              Horário bloqueado
            </p>
            <p className="mt-1 text-sm text-slate-500">{formattedDate}</p>
          </div>
        </div>

        {viewState.type === "loading" && (
          <p className="mt-6 text-sm text-slate-500">Carregando detalhes...</p>
        )}

        {viewState.type === "error" && (
          <div className="mt-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-left">
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
              aria-hidden
            />
            <p className="text-sm leading-relaxed text-amber-900">
              {viewState.message}
            </p>
          </div>
        )}

        {viewState.type === "ready" && isBlocked && (
          <div className="mt-6 text-left">
            {lockNote ? (
              <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-700">
                  <MessageSquareText className="h-3.5 w-3.5" aria-hidden />
                  Motivo do bloqueio
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {lockNote}
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Bloqueio sem observações.
              </p>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isUnlocking}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            Fechar
          </button>

          {(isBlocked || slotMissing) && viewState.type !== "error" && (
            <button
              type="button"
              onClick={onUnlock}
              disabled={isUnlocking || viewState.type === "loading"}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-60"
            >
              {isUnlocking ? "Desbloqueando..." : "Desbloquear horário"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
